import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Calculator,
  Plus,
  Trash2,
  Edit3,
  Shield,
  Eye,
  DollarSign,
  FileText,
  Users,
  Loader2,
  CheckCircle,
  XCircle,
  Mail,
  Phone,
  Copy
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Available permissions for accountants
const PERMISSION_OPTIONS = [
  { id: "view_payouts", label: "View Payout Dashboard", icon: Eye, description: "View pending and approved payouts" },
  { id: "process_payouts", label: "Process Payouts", icon: DollarSign, description: "Approve and send payouts via Stripe" },
  { id: "view_payout_history", label: "View Payout History", icon: FileText, description: "Access historical payout records" },
  { id: "view_soldier_stats", label: "View Soldier Stats", icon: Users, description: "View soldier referral statistics" },
  { id: "export_payout_reports", label: "Export Reports", icon: FileText, description: "Download payout data as CSV/JSON" },
];

const AccountantManagement = ({ token }) => {
  const [loading, setLoading] = useState(true);
  const [accountants, setAccountants] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAccountant, setSelectedAccountant] = useState(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tempPassword, setTempPassword] = useState("");
  
  // Form state
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    phone: "",
    permissions: ["view_payouts", "view_payout_history"]
  });

  useEffect(() => {
    if (token) {
      fetchAccountants();
    }
  }, [token]);

  const fetchAccountants = async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/admin/accountants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccountants(response.data.accountants || []);
    } catch (error) {
      console.error("Failed to fetch accountants:", error);
      toast.error("Failed to load accountants");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.email || !formData.name) {
      toast.error("Email and name are required");
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/admin/accountants`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setTempPassword(response.data.temp_password);
      toast.success("Accountant created successfully!");
      fetchAccountants();
      // Don't close modal yet - show temp password
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create accountant");
    } finally {
      setCreating(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedAccountant) return;

    setSaving(true);
    try {
      await axios.put(`${API}/admin/accountants/${selectedAccountant.user_id}`, {
        permissions: formData.permissions,
        is_active: formData.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Accountant updated successfully!");
      setShowEditModal(false);
      fetchAccountants();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update accountant");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!confirm("Are you sure you want to remove this accountant?")) return;

    try {
      await axios.delete(`${API}/admin/accountants/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Accountant removed successfully");
      fetchAccountants();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove accountant");
    }
  };

  const openEditModal = (accountant) => {
    setSelectedAccountant(accountant);
    setFormData({
      email: accountant.email,
      name: accountant.name,
      phone: accountant.phone || "",
      permissions: accountant.accountant_permissions || [],
      is_active: accountant.is_active !== false
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      email: "",
      name: "",
      phone: "",
      permissions: ["view_payouts", "view_payout_history"]
    });
    setTempPassword("");
  };

  const togglePermission = (permId) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permId)
        ? prev.permissions.filter(p => p !== permId)
        : [...prev.permissions, permId]
    }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Calculator className="w-6 h-6 text-green-400" />
            Accountant Management
          </h2>
          <p className="text-zinc-400 text-sm mt-1">
            Manage accountants with limited access to process soldier payouts
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          className="bg-gradient-to-r from-green-500 to-emerald-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Accountant
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-zinc-400">
              <p className="font-medium text-white mb-1">About Accountant Role</p>
              <p>
                Accountants have <strong className="text-amber-400">limited access</strong> to manage soldier payouts only. 
                They can view the payout ledger, approve payouts, and process payments via Stripe - 
                but cannot access other admin features like user management or platform settings.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Accountants List */}
      {accountants.length === 0 ? (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="py-12 text-center">
            <Calculator className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">No accountants added yet</p>
            <p className="text-zinc-500 text-sm mt-1">
              Add an accountant to help manage soldier payouts
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {accountants.map((accountant) => (
            <Card key={accountant.user_id} className="bg-zinc-900 border-zinc-800">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                      <Calculator className="w-6 h-6 text-green-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-white font-medium">{accountant.name}</p>
                        {accountant.is_active !== false ? (
                          <Badge className="bg-green-500/20 text-green-400 text-xs">Active</Badge>
                        ) : (
                          <Badge className="bg-red-500/20 text-red-400 text-xs">Inactive</Badge>
                        )}
                      </div>
                      <p className="text-zinc-400 text-sm">{accountant.email}</p>
                      <div className="flex gap-1 mt-2">
                        {(accountant.accountant_permissions || []).slice(0, 3).map(perm => (
                          <Badge key={perm} variant="outline" className="text-xs border-zinc-700 text-zinc-400">
                            {perm.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                        {(accountant.accountant_permissions?.length || 0) > 3 && (
                          <Badge variant="outline" className="text-xs border-zinc-700 text-zinc-400">
                            +{accountant.accountant_permissions.length - 3} more
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditModal(accountant)}
                      className="border-zinc-700 text-zinc-300"
                    >
                      <Edit3 className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(accountant.user_id)}
                      className="border-red-800 text-red-400 hover:bg-red-500/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={(open) => {
        if (!open) resetForm();
        setShowCreateModal(open);
      }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-green-400" />
              {tempPassword ? "Accountant Created!" : "Add New Accountant"}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {tempPassword 
                ? "Share these credentials with the accountant securely"
                : "Create a new accountant with access to soldier payouts"
              }
            </DialogDescription>
          </DialogHeader>

          {tempPassword ? (
            // Show credentials
            <div className="space-y-4 py-4">
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <p className="text-green-400 font-medium mb-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Account Created Successfully
                </p>
                <div className="space-y-3">
                  <div>
                    <Label className="text-zinc-400 text-xs">Email</Label>
                    <div className="flex items-center gap-2">
                      <Input value={formData.email} readOnly className="bg-zinc-800 border-zinc-700" />
                      <Button size="sm" variant="outline" onClick={() => copyToClipboard(formData.email)}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Temporary Password</Label>
                    <div className="flex items-center gap-2">
                      <Input value={tempPassword} readOnly className="bg-zinc-800 border-zinc-700 font-mono" />
                      <Button size="sm" variant="outline" onClick={() => copyToClipboard(tempPassword)}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                <p className="text-amber-400 text-xs mt-3">
                  ⚠️ This password will not be shown again. The accountant should change it after first login.
                </p>
              </div>
              <DialogFooter>
                <Button onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }} className="w-full bg-green-500 hover:bg-green-600 text-black">
                  Done
                </Button>
              </DialogFooter>
            </div>
          ) : (
            // Create form
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Name *</Label>
                  <Input
                    placeholder="John Smith"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Phone</Label>
                  <Input
                    placeholder="(555) 123-4567"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({...prev, phone: e.target.value}))}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-300">Email *</Label>
                <Input
                  type="email"
                  placeholder="accountant@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({...prev, email: e.target.value}))}
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>

              <div className="space-y-3">
                <Label className="text-zinc-300">Permissions</Label>
                <div className="space-y-2">
                  {PERMISSION_OPTIONS.map(perm => (
                    <div
                      key={perm.id}
                      className="flex items-center space-x-3 p-3 bg-zinc-800/50 rounded-lg cursor-pointer hover:bg-zinc-800"
                      onClick={() => togglePermission(perm.id)}
                    >
                      <Checkbox
                        checked={formData.permissions.includes(perm.id)}
                        onCheckedChange={() => togglePermission(perm.id)}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <perm.icon className="w-4 h-4 text-zinc-400" />
                          <span className="text-white text-sm">{perm.label}</span>
                        </div>
                        <p className="text-zinc-500 text-xs">{perm.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreateModal(false)} className="border-zinc-700">
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreate} 
                  disabled={creating}
                  className="bg-green-500 hover:bg-green-600 text-black"
                >
                  {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  Create Accountant
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="w-5 h-5 text-amber-400" />
              Edit Accountant
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Update permissions for {selectedAccountant?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
              <div>
                <Label className="text-zinc-300">Account Status</Label>
                <p className="text-zinc-500 text-xs">Enable or disable this accountant&apos;s access</p>
              </div>
              <Switch
                checked={formData.is_active !== false}
                onCheckedChange={(v) => setFormData(prev => ({...prev, is_active: v}))}
              />
            </div>

            <div className="space-y-3">
              <Label className="text-zinc-300">Permissions</Label>
              <div className="space-y-2">
                {PERMISSION_OPTIONS.map(perm => (
                  <div
                    key={perm.id}
                    className="flex items-center space-x-3 p-3 bg-zinc-800/50 rounded-lg cursor-pointer hover:bg-zinc-800"
                    onClick={() => togglePermission(perm.id)}
                  >
                    <Checkbox
                      checked={formData.permissions.includes(perm.id)}
                      onCheckedChange={() => togglePermission(perm.id)}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <perm.icon className="w-4 h-4 text-zinc-400" />
                        <span className="text-white text-sm">{perm.label}</span>
                      </div>
                      <p className="text-zinc-500 text-xs">{perm.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditModal(false)} className="border-zinc-700">
                Cancel
              </Button>
              <Button 
                onClick={handleUpdate} 
                disabled={saving}
                className="bg-amber-500 hover:bg-amber-600 text-black"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Save Changes
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountantManagement;
